/**
 * Sentry browser-SDK init + PII scrubbing for CandidCheck.
 *
 * Mirrors backend/observability.py — same denylist, same URL rewriter, same
 * fail-degraded per-step try/catch posture. The frontend's PII surface is
 * narrower (no SQL breadcrumbs, no direct DB params, no candidate work-product
 * by reference) but still leaks admin_token via fetch URLs + breadcrumbs.
 */

import * as Sentry from "@sentry/react";

// Exact key names of PII fields that might appear in component state, query
// breadcrumbs, or captured exception context. Mirrors backend denylist.
const PII_KEY_EXACT: ReadonlySet<string> = new Set(
  [
    "candidate_name",
    "candidate_email",
    "name_normalized",
    "email_normalized",
    // FEAT-23: a recruiter's role in the company (sign-up + profile bodies);
    // their phone number is the `phone` pattern below.
    "job_title",
    "ip",
    "ip_first_seen",
    "user_agent",
    "admin_token",
    "admin_tokens",
    "candidate_token",
    "admin_token_hash",
    "candidate_token_hash",
    "final_code",
    "code",
    "selected_answer",
    "selected_answers",
    "final_test_results",
    "hidden_test_results",
    "code_snapshot",
    // FEAT-31: the two text-bearing keys of a code-replay chunk.
    "replay_base",
    "replay_steps",
    // EF-10 / EF-5: the candidate's prose answer (and its /question echo) and
    // the recruiter's correction note — free text, recruiter-only.
    "free_text",
    "saved_free_text",
    "manual_score_reason",
    // Attempt-id request header — a soft credential (shared-link attempts are
    // keyed by it); mirror of backend denylist.
    "x-attempt-id",
  ].map((s) => s.toLowerCase()),
);

// Substring wildcards as a safety net for new fields with PII-shaped names.
const PII_KEY_PATTERNS: readonly RegExp[] = [
  /candidate/i,
  /email/i,
  /_token/i,
  // Any *secret* key. MIRROR of backend `_PII_KEY_PATTERNS` — and the browser
  // is where it matters most: the recruiter TYPES the Traffit `client_secret`
  // into a form here, so a failed /api/admin/ats/traffit/connect could carry
  // the plaintext OAuth secret into a Sentry breadcrumb that the backend would
  // have redacted. A client_secret unlocks the tenant's whole ATS. The two
  // lists are pinned equal by test_observability_redaction.
  /secret/i,
  // FEAT-23: a phone number typed into sign-up, the profile, or a candidate's
  // intake form. Mirror of the backend line (it also catches `microphone…`).
  /phone/i,
];

// URL rewriters for the two recruiter-token shapes that leak admin_token.
// The negative lookahead leaves every LITERAL first segment under /api/admin/
// readable, but only when it forms a COMPLETE path segment — a token that
// merely starts with one of those words is still redacted.
//
// The list is every literal first segment the backend serves under
// `/api/admin/` (everything that is NOT the `{admin_token}` router), taken
// from the app's OpenAPI output. It is a mirror of `_ADMIN_LITERAL_SEGMENTS`
// in backend/observability.py and MUST carry the same names in the same
// order: the backend suite
// (`test_observability_redaction.test_admin_literal_lookahead_matches_the_apps_routes`)
// reads this file and fails if the two diverge. It drifted once — `ats` was
// added to the backend when the Traffit routes landed and not here, so every
// browser event for `/api/admin/ats/...` came out `/api/admin/<redacted>/...`
// (2026-09-08). A missing name only over-redacts; adding one is always safe,
// because an admin_token can only ever be the FIRST segment and literal routes
// match before `{admin_token}`.
const ADMIN_TOKEN_URL_RE =
  /(\/api\/admin\/)(?!(?:analyze-jd|ats|branding|build-test|candidates|company-details|company-settings|draft-test-messaging|export-settings|fetch-job-offer|generate-questions|integrations|jobs|mcp|me|overview|pending-follow-ups|question-images|questions|report-problem|role-permissions|soft-skills|statistics|support-attachments|support-requests|tags|team|templates|tests|workspace)(?:[/?#]|$))[^/?#]+/g;
// The SPA mirrors of the same secret. `/admin/<token>`, `/admin/<token>/edit`,
// `/admin/<token>/preview` and `/admin/<token>/attempts/<id>` are real routes
// (App.tsx), so `event.request.url` and every navigation breadcrumb on those
// pages carried the admin_token verbatim — the rule above is anchored on
// `/api/admin/` and never saw the SPA shape. `(?<!/api)` keeps the two rules
// from overlapping. Keep the literal list in sync with App.tsx's literal
// `/admin/<seg>` routes, which MUST precede `/admin/:token` there for the same
// reason they are excluded here.
const ADMIN_TOKEN_SPA_URL_RE =
  /(?<!\/api)(\/admin\/)(?!(?:backoffice|billing|candidates|faq|home|integrations|jobs|login|me|new|questions|settings|statistics|support|tags|team|templates|tests)(?:[/?#]|$))[^/?#]+/g;
const ADMIN_TOKENS_QUERY_RE = /(admin_tokens=)[^&]+/g;
// Candidate tokens ride in the URL on EVERY candidate request — the API
// (`/api/t/<token>/...`) and the SPA routes (`/t/<token>`, `/t/<token>/practice`)
// — and the token IS the bearer credential. One pattern covers both shapes: the
// token is always the first segment after a literal `/t/` (no literal routes
// live directly under `/t/`); sub-routes stay readable. The attempt-id is a
// soft credential too and can ride as `?attempt_id=` on the sendBeacon
// unload-flush fallback. Mirror of backend/observability.py.
const CANDIDATE_TOKEN_URL_RE = /(\/t\/)[^/?#]+/g;
const ATTEMPT_ID_QUERY_RE = /(attempt_id=)[^&#]+/g;
// The emailed account links — /accept-invite, /verify-email, /reset-password —
// carry their one-time secret as `?token=` (a reset token is account-takeover
// material), and these routes read it from the query, so the full URL would
// otherwise reach Sentry's request URL and navigation breadcrumbs. Anchored on
// the parameter NAME (`?token=` / `&token=` and any `*_token=`); `admin_tokens=`
// keeps its own rule above. Mirror of `_TOKEN_QUERY_RE` in backend/observability.py.
const TOKEN_QUERY_RE = /((?:^|[?&])(?:[A-Za-z0-9-]+_)?token=)[^&#\s"']*/g;
// FEAT-48: the emailed SIGN-IN link carries its one-time secret in the PATH
// (`/auth/link/<token>`), and that token signs the person in. Always the first
// segment after a literal `/auth/link/`, nothing else lives under it. Mirror
// of `_SIGN_IN_LINK_URL_RE` in backend/observability.py.
const SIGN_IN_LINK_URL_RE = /(\/auth\/(?:link|join)\/)[^/?#]+/g;
// EF-3: the identity vendor returns the candidate to /identity/done with
// `?verificationSessionId=…&status=…`. The page strips it on mount, but the
// first navigation breadcrumb can carry it — drop the whole query. Mirror of
// `_IDENTITY_DONE_QUERY_RE` in backend/observability.py.
const IDENTITY_DONE_QUERY_RE = /(\/identity\/done)\?[^\s"'#]*/g;
const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;

const REDACTED = "<redacted>";

/**
 * Strip the secret token segment from every tokenized URL shape:
 * /api/admin/<token>/, /api/t/<token>/, the /t/<token> SPA routes,
 * the /auth/link/<token> sign-in link (FEAT-48), ?admin_tokens=<csv>,
 * ?attempt_id= and ?token= values (the emailed accept-invite / verify-email /
 * reset-password links).
 * Safe to call on any string (URL, query fragment, breadcrumb URL).
 * Exported for reuse in api.ts's jsonOrThrow / okOrThrow instrumentation.
 */
export function redactUrl(url: string | null | undefined): string {
  if (!url) return url ?? "";
  return url
    .replace(ADMIN_TOKEN_URL_RE, `$1${REDACTED}`)
    .replace(ADMIN_TOKEN_SPA_URL_RE, `$1${REDACTED}`)
    .replace(ADMIN_TOKENS_QUERY_RE, `$1${REDACTED}`)
    .replace(CANDIDATE_TOKEN_URL_RE, `$1${REDACTED}`)
    .replace(SIGN_IN_LINK_URL_RE, `$1${REDACTED}`)
    .replace(TOKEN_QUERY_RE, `$1${REDACTED}`)
    .replace(ATTEMPT_ID_QUERY_RE, `$1${REDACTED}`)
    .replace(IDENTITY_DONE_QUERY_RE, "$1");
}

function keyIsPii(key: string): boolean {
  if (PII_KEY_EXACT.has(key.toLowerCase())) return true;
  for (const pattern of PII_KEY_PATTERNS) {
    if (pattern.test(key)) return true;
  }
  return false;
}

function scrubRecursive(obj: unknown): unknown {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(scrubRecursive);
  if (typeof obj === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      out[k] = keyIsPii(k) ? REDACTED : scrubRecursive(v);
    }
    return out;
  }
  return obj;
}

function scrubMessageString(s: string): string {
  return s.replace(EMAIL_RE, "<redacted-email>");
}

// FRONTEND-REACT-B: Monaco's Safari clipboard workaround
// (`installWebKitWriteTextWorkaround`, monaco-editor 0.55) cancels its own
// pending ClipboardItem promise on every click and keydown, and nothing handles
// the rejection — an unhandled "Canceled" with no user effect. Dropped only when
// the error was RAISED inside the vendored bundle (the top frame): the SDK's
// listener wrapper sits below it in our own bundle, so "every frame is Monaco"
// would never match, and our own "Canceled" errors still report.
const MONACO_VENDOR_PATH = "/monaco-v3/";

function isMonacoCancellation(event: Sentry.ErrorEvent): boolean {
  const values = event.exception?.values;
  if (!values || values.length === 0) return false;
  return values.every((exc) => {
    if (exc.type !== "Canceled" || exc.value !== "Canceled") return false;
    // Sentry orders frames oldest-first: the last one raised the error.
    const frames = exc.stacktrace?.frames;
    const top = frames?.[frames.length - 1];
    return (top?.filename ?? top?.abs_path ?? "").includes(MONACO_VENDOR_PATH);
  });
}

/** Sentry beforeSend — each step in its own try/catch (partial scrub > leak).
 * Exported (with underscore prefix) for unit tests only — not for app code. */
export function _beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  try {
    if (isMonacoCancellation(event)) return null;
  } catch {
    // Unsure → keep it: a missed drop is noise, a wrong drop loses an error.
  }

  try {
    if (event.request) {
      if (typeof event.request.url === "string") {
        event.request.url = redactUrl(event.request.url);
      }
      if (typeof event.request.query_string === "string") {
        event.request.query_string = redactUrl(event.request.query_string);
      }
      if (event.request.data !== undefined) {
        event.request.data = scrubRecursive(event.request.data) as typeof event.request.data;
      }
      if (event.request.headers && typeof event.request.headers === "object") {
        // Key-name scrub, then the SAME URL rewrite as `request.url` over every
        // string value: Sentry's HttpContext integration stamps
        // `Referer: document.referrer` on every event, and on the attempts page
        // that is `/admin/<token>` verbatim — the S3 shape on the same event
        // whose url was just redacted (2026-09-08 verify).
        const scrubbed = scrubRecursive(event.request.headers) as Record<string, unknown>;
        for (const key of Object.keys(scrubbed)) {
          const value = scrubbed[key];
          if (typeof value === "string") scrubbed[key] = redactUrl(value);
        }
        event.request.headers = scrubbed as typeof event.request.headers;
      }
    }
  } catch {
    // OPS-28 #25: a scrubber bug must never ship the UNSCRUBBED field. Each
    // pass drops what it could not clean rather than leaving it as-is; the
    // event still goes out, minus that part (fail-partial, never fail-open).
    delete event.request;
  }

  try {
    if (event.extra) event.extra = scrubRecursive(event.extra) as typeof event.extra;
    if (event.contexts) event.contexts = scrubRecursive(event.contexts) as typeof event.contexts;
    if (event.tags) event.tags = scrubRecursive(event.tags) as typeof event.tags;
    if (event.user) event.user = scrubRecursive(event.user) as typeof event.user;
  } catch {
    delete event.extra;
    delete event.contexts;
    delete event.tags;
    delete event.user;
  }

  try {
    if (event.breadcrumbs) {
      for (const crumb of event.breadcrumbs) {
        if (crumb.data && typeof crumb.data === "object") {
          if (typeof crumb.data.url === "string") {
            crumb.data.url = redactUrl(crumb.data.url);
          }
          // Navigation breadcrumbs carry the SPA route in `from`/`to` — the
          // exact place a candidate-token /t/<token> route would surface.
          if (typeof crumb.data.from === "string") {
            crumb.data.from = redactUrl(crumb.data.from);
          }
          if (typeof crumb.data.to === "string") {
            crumb.data.to = redactUrl(crumb.data.to);
          }
        }
        if (typeof crumb.message === "string") {
          crumb.message = scrubMessageString(crumb.message);
        }
      }
    }
  } catch {
    delete event.breadcrumbs;
  }

  try {
    if (event.exception?.values) {
      for (const exc of event.exception.values) {
        if (typeof exc.value === "string") {
          exc.value = scrubMessageString(exc.value);
        }
      }
    }
  } catch {
    // The stack frames stay (file/line, no values); only the messages, which
    // are the part that can carry a token or an address, are dropped.
    if (event.exception?.values) {
      for (const exc of event.exception.values) delete exc.value;
    }
  }

  try {
    if (typeof event.message === "string") {
      event.message = scrubMessageString(event.message);
    }
  } catch {
    delete event.message;
  }

  return event;
}

const DROPPED_BREADCRUMB_CATEGORIES: ReadonlySet<string> = new Set([
  "query",
  "db.sql",
  "sql.query",
]);

const URL_BREADCRUMB_CATEGORIES: ReadonlySet<string> = new Set([
  "fetch",
  "xhr",
  "http",
]);

/** Drop SQL-shaped breadcrumbs (defense-in-depth); rewrite admin_token in HTTP URLs.
 * Exported (with underscore prefix) for unit tests only — not for app code. */
function isCandidatePage(): boolean {
  try {
    return typeof window !== "undefined" && window.location.pathname.startsWith("/t/");
  } catch {
    return true; // unsure → treat as a candidate page (drop)
  }
}

export function _beforeBreadcrumb(crumb: Sentry.Breadcrumb): Sentry.Breadcrumb | null {
  try {
    const category = crumb.category ?? "";
    if (DROPPED_BREADCRUMB_CATEGORIES.has(category)) return null;
    // Pre-ship 2026-10-18 (appsec h08): in-browser Python prints through
    // console.log, so on a candidate page a console breadcrumb is the
    // candidate's own program output — riding every OPS-28 warning event
    // there. Candidate work product never leaves the browser this way.
    if (category === "console" && isCandidatePage()) return null;
    if (URL_BREADCRUMB_CATEGORIES.has(category)) {
      if (crumb.data && typeof crumb.data === "object" && typeof crumb.data.url === "string") {
        crumb.data.url = redactUrl(crumb.data.url);
      }
    }
    if (category === "navigation" && crumb.data && typeof crumb.data === "object") {
      // SPA route changes land here as `from`/`to` — where /t/<token> surfaces.
      if (typeof crumb.data.from === "string") crumb.data.from = redactUrl(crumb.data.from);
      if (typeof crumb.data.to === "string") crumb.data.to = redactUrl(crumb.data.to);
    }
    return crumb;
  } catch {
    return null;
  }
}

/**
 * Initialize Sentry browser SDK if VITE_SENTRY_DSN is present at build time.
 * No-op when DSN is missing (local dev without an .env override).
 *
 * Must be called once at module load, BEFORE React mounts (so Sentry can
 * patch fetch + window error handlers before any app code runs).
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? "production",
    release: (import.meta.env.VITE_RELEASE as string | undefined) ?? "unknown",
    sampleRate: 1.0,
    tracesSampleRate: 0,
    sendDefaultPii: false,
    maxBreadcrumbs: 50,
    beforeSend: _beforeSend,
    beforeBreadcrumb: _beforeBreadcrumb,
    denyUrls: [/extensions\//, /^chrome:\/\//, /^moz-extension:\/\//],
  });
}

/**
 * Capture an API-layer error with redacted URL context. Skips 4xx (low signal)
 * and transient gateway codes (502/503/504): idempotent reads already retry those
 * in `apiFetch`, so anything reaching here is a lingering blip or a non-retried
 * write — infra transients, not app bugs (Fly proxy / Neon cold start). Genuine
 * 500s (real server faults) still report.
 */
export function captureApiError(
  err: unknown,
  context: { url: string; status: number; method?: string },
): void {
  if (context.status < 500) return;
  if (context.status === 502 || context.status === 503 || context.status === 504)
    return;
  Sentry.captureException(err, {
    tags: {
      api_status: String(context.status),
      api_path: redactUrl(context.url),
      api_method: context.method ?? "",
    },
  });
}

/**
 * OPS-28: a failure the UI survived but nobody would otherwise hear about — an
 * answer save abandoned after its retries, telemetry dropped, a snapshot lost.
 * One Sentry message per call, tagged with CLOSED VOCABULARY only (a kind, a
 * reason, a count as a string): never an answer, an email, a name or a token —
 * the message and the tags are what the scrubber above cannot be trusted to
 * catch, so the caller keeps them clean by construction.
 */
export function captureSilentFailure(
  message: string,
  tags: Record<string, string | number | boolean> = {},
): void {
  const clean: Record<string, string> = {};
  for (const [k, v] of Object.entries(tags)) clean[k] = String(v);
  Sentry.captureMessage(message, { level: "warning", tags: clean });
}
